import {
  CheckIcon,
  Combobox,
  Group,
  Input,
  Pill,
  PillsInput,
  Text,
  ThemeIcon,
  useCombobox,
} from "@mantine/core";
import styles from "core/components/statusSelectBox/StatusSelect.module.scss";

// Multi-select whose options and selected pills show each item's icon, which
// Mantine's MultiSelect can't do for pills. Built like StatusSelectBox, but
// controlled (value/onChange) so it works with form.getInputProps.
//
// data: [{ value, label, icon, color, description? }]

const ItemIcon = ({ item }) => (
  <ThemeIcon size="sm" variant="light" color={item.color}>
    {item.icon}
  </ThemeIcon>
);

const IconSelectBox = ({
  data,
  value = [],
  onChange,
  placeholder = "Pick value",
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const selected = value ?? [];

  const handleValueSelect = (val) =>
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val]
    );

  const handleValueRemove = (val) =>
    onChange(selected.filter((v) => v !== val));

  const pills = selected
    .map((val) => data.find((item) => item.value === val))
    .filter(Boolean)
    .map((item) => (
      <Pill
        className={styles.pill}
        key={item.value}
        withRemoveButton
        onRemove={() => handleValueRemove(item.value)}
      >
        <Group gap={6} wrap="nowrap">
          <ItemIcon item={item} />
          <Text size="xs" fw={600}>
            {item.label}
          </Text>
        </Group>
      </Pill>
    ));

  const options = data.map((item) => (
    <Combobox.Option
      value={item.value}
      key={item.value}
      active={selected.includes(item.value)}
    >
      <Group gap="sm" wrap="nowrap">
        {selected.includes(item.value) ? <CheckIcon size={12} /> : null}
        <ItemIcon item={item} />
        <div>
          <Text size="sm">{item.label}</Text>
          {item.description ? (
            <Text size="xs" c="dimmed">
              {item.description}
            </Text>
          ) : null}
        </div>
      </Group>
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={false}
    >
      <Combobox.DropdownTarget>
        <PillsInput pointer onClick={() => combobox.toggleDropdown()}>
          <Pill.Group>
            {pills.length ? (
              pills
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
            <Combobox.EventsTarget>
              <PillsInput.Field
                type="hidden"
                onBlur={() => combobox.closeDropdown()}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && selected.length) {
                    event.preventDefault();
                    handleValueRemove(selected[selected.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default IconSelectBox;
